import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, FloppyDisk, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { refreshGeneralSettings } from "@/hooks/useGeneralSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const TIMEZONES = [
  { v: "America/Sao_Paulo", label: "Brasília / São Paulo (BRT) GMT-3" },
  { v: "America/Manaus", label: "Manaus / Cuiabá (AMT) GMT-4" },
  { v: "America/Rio_Branco", label: "Rio Branco / Acre (ACT) GMT-5" },
  { v: "America/Noronha", label: "Fernando de Noronha GMT-2" },
  { v: "America/Belem", label: "Belém / Pará GMT-3" },
  { v: "America/Fortaleza", label: "Fortaleza / Ceará GMT-3" },
  { v: "America/Recife", label: "Recife / Pernambuco GMT-3" },
  { v: "America/Bahia", label: "Salvador / Bahia GMT-3" },
  { v: "America/Maceio", label: "Maceió / Alagoas GMT-3" },
  { v: "America/Araguaina", label: "Araguaína / Tocantins GMT-3" },
  { v: "America/Campo_Grande", label: "Campo Grande / MS GMT-4" },
  { v: "America/Cuiaba", label: "Cuiabá / MT GMT-4" },
  { v: "America/Porto_Velho", label: "Porto Velho / RO GMT-4" },
  { v: "America/Boa_Vista", label: "Boa Vista / RR GMT-4" },
  { v: "America/Eirunepe", label: "Eirunepé / AM GMT-5" },
  { v: "UTC", label: "UTC / GMT+0" },
  { v: "America/New_York", label: "New York / EST GMT-5" },
  { v: "America/Chicago", label: "Chicago / CST GMT-6" },
  { v: "America/Denver", label: "Denver / MST GMT-7" },
  { v: "America/Los_Angeles", label: "Los Angeles / PST GMT-8" },
  { v: "America/Mexico_City", label: "Ciudad de México GMT-6" },
  { v: "America/Buenos_Aires", label: "Buenos Aires / Argentina GMT-3" },
  { v: "America/Santiago", label: "Santiago / Chile GMT-4" },
  { v: "America/Bogota", label: "Bogotá / Colômbia GMT-5" },
  { v: "America/Lima", label: "Lima / Peru GMT-5" },
  { v: "America/Caracas", label: "Caracas / Venezuela GMT-4" },
  { v: "America/La_Paz", label: "La Paz / Bolívia GMT-4" },
  { v: "America/Asuncion", label: "Assunção / Paraguai GMT-3" },
  { v: "America/Montevideo", label: "Montevidéu / Uruguai GMT-3" },
  { v: "Europe/Lisbon", label: "Lisboa / Portugal GMT+0" },
  { v: "Europe/London", label: "Londres / Reino Unido GMT+0" },
  { v: "Europe/Madrid", label: "Madrid / Espanha GMT+1" },
  { v: "Europe/Paris", label: "Paris / França GMT+1" },
  { v: "Europe/Berlin", label: "Berlim / Alemanha GMT+1" },
  { v: "Europe/Rome", label: "Roma / Itália GMT+1" },
  { v: "Europe/Amsterdam", label: "Amsterdam / Holanda GMT+1" },
  { v: "Europe/Zurich", label: "Zurique / Suíça GMT+1" },
  { v: "Europe/Moscow", label: "Moscou / Rússia GMT+3" },
  { v: "Africa/Cairo", label: "Cairo / Egito GMT+2" },
  { v: "Africa/Johannesburg", label: "Joanesburgo / África do Sul GMT+2" },
  { v: "Asia/Dubai", label: "Dubai / EAU GMT+4" },
  { v: "Asia/Tokyo", label: "Tóquio / Japão GMT+9" },
  { v: "Asia/Shanghai", label: "Xangai / China GMT+8" },
  { v: "Asia/Hong_Kong", label: "Hong Kong GMT+8" },
  { v: "Asia/Singapore", label: "Singapura GMT+8" },
  { v: "Asia/Kolkata", label: "Nova Deli / Índia GMT+5:30" },
  { v: "Australia/Sydney", label: "Sydney / Austrália GMT+11" },
  { v: "Pacific/Auckland", label: "Auckland / Nova Zelândia GMT+13" },
];

const COUNTRIES = [
  { v: "+55", label: "🇧🇷 Brasil (+55)" },
  { v: "+1", label: "🇺🇸 EUA / Canadá (+1)" },
  { v: "+52", label: "🇲🇽 México (+52)" },
  { v: "+54", label: "🇦🇷 Argentina (+54)" },
  { v: "+56", label: "🇨🇱 Chile (+56)" },
  { v: "+57", label: "🇨🇴 Colômbia (+57)" },
  { v: "+58", label: "🇻🇪 Venezuela (+58)" },
  { v: "+51", label: "🇵🇪 Peru (+51)" },
  { v: "+591", label: "🇧🇴 Bolívia (+591)" },
  { v: "+593", label: "🇪🇨 Equador (+593)" },
  { v: "+595", label: "🇵🇾 Paraguai (+595)" },
  { v: "+598", label: "🇺🇾 Uruguai (+598)" },
  { v: "+506", label: "🇨🇷 Costa Rica (+506)" },
  { v: "+507", label: "🇵🇦 Panamá (+507)" },
  { v: "+53", label: "🇨🇺 Cuba (+53)" },
  { v: "+34", label: "🇪🇸 Espanha (+34)" },
  { v: "+351", label: "🇵🇹 Portugal (+351)" },
  { v: "+33", label: "🇫🇷 França (+33)" },
  { v: "+44", label: "🇬🇧 Reino Unido (+44)" },
  { v: "+49", label: "🇩🇪 Alemanha (+49)" },
  { v: "+39", label: "🇮🇹 Itália (+39)" },
  { v: "+31", label: "🇳🇱 Holanda (+31)" },
  { v: "+41", label: "🇨🇭 Suíça (+41)" },
  { v: "+43", label: "🇦🇹 Áustria (+43)" },
  { v: "+32", label: "🇧🇪 Bélgica (+32)" },
  { v: "+45", label: "🇩🇰 Dinamarca (+45)" },
  { v: "+46", label: "🇸🇪 Suécia (+46)" },
  { v: "+47", label: "🇳🇴 Noruega (+47)" },
  { v: "+358", label: "🇫🇮 Finlândia (+358)" },
  { v: "+353", label: "🇮🇪 Irlanda (+353)" },
  { v: "+30", label: "🇬🇷 Grécia (+30)" },
  { v: "+48", label: "🇵🇱 Polônia (+48)" },
  { v: "+7", label: "🇷🇺 Rússia (+7)" },
  { v: "+90", label: "🇹🇷 Turquia (+90)" },
  { v: "+380", label: "🇺🇦 Ucrânia (+380)" },
  { v: "+971", label: "🇦🇪 EAU (+971)" },
  { v: "+972", label: "🇮🇱 Israel (+972)" },
  { v: "+966", label: "🇸🇦 Arábia Saudita (+966)" },
  { v: "+20", label: "🇪🇬 Egito (+20)" },
  { v: "+27", label: "🇿🇦 África do Sul (+27)" },
  { v: "+234", label: "🇳🇬 Nigéria (+234)" },
  { v: "+91", label: "🇮🇳 Índia (+91)" },
  { v: "+81", label: "🇯🇵 Japão (+81)" },
  { v: "+82", label: "🇰🇷 Coreia do Sul (+82)" },
  { v: "+86", label: "🇨🇳 China (+86)" },
  { v: "+852", label: "🇭🇰 Hong Kong (+852)" },
  { v: "+65", label: "🇸🇬 Singapura (+65)" },
  { v: "+60", label: "🇲🇾 Malásia (+60)" },
  { v: "+66", label: "🇹🇭 Tailândia (+66)" },
  { v: "+62", label: "🇮🇩 Indonésia (+62)" },
  { v: "+63", label: "🇵🇭 Filipinas (+63)" },
  { v: "+61", label: "🇦🇺 Austrália (+61)" },
  { v: "+64", label: "🇳🇿 Nova Zelândia (+64)" },
];

function Section({ title, description, right }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr,340px] gap-3 items-start p-4 bg-white border border-[#E5E5E0]">
      <div>
        <h3 className="text-sm font-black uppercase tracking-wider">{title}</h3>
        <p className="text-xs text-[#5C5C59] mt-1 leading-snug">{description}</p>
      </div>
      <div className="min-w-0">{right}</div>
    </div>
  );
}

export default function CompanyGeneralSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const load = async () => {
    try {
      const [{ data }, mfa, ai] = await Promise.all([
        api.get("/general-settings"),
        api.get("/mfa/status").catch(() => ({ data: { mfa_enabled: false } })),
        api.get("/company/ai-settings").catch(() => ({ data: {} })),
      ]);
      setS({
        ...data,
        mfa_enabled: mfa.data.mfa_enabled,
        // pull existing "assinatura" + "encerramento" from ai_settings
        signature_enabled: !!ai.data.signature_enabled,
        signature_template: ai.data.signature_template || "*{name}*\n{message}",
        close_message: ai.data.close_message || "",
        send_rating_request: !!ai.data.send_rating_request,
        rating_prompt: ai.data.rating_prompt || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setS((prev) => ({ ...(prev || {}), ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const {
        signature_enabled, signature_template, close_message,
        send_rating_request, rating_prompt, mfa_enabled, ...general
      } = s || {};
      // Fetch current AI settings to preserve enabled/prompt/model/handoff_keywords
      const { data: ai } = await api.get("/company/ai-settings").catch(() => ({ data: {} }));
      await Promise.all([
        api.put("/general-settings", general),
        api.put("/company/ai-settings", {
          enabled: !!ai.enabled,
          prompt: ai.prompt || "",
          model: ai.model || "claude-sonnet-4-6",
          handoff_keywords: ai.handoff_keywords || [],
          signature_enabled: !!signature_enabled,
          signature_template: signature_template || "*{name}*\n{message}",
          close_message: close_message || "",
          send_rating_request: !!send_rating_request,
          rating_prompt: rating_prompt || "",
        }),
      ]);
      refreshGeneralSettings();
      toast.success("Ajustes salvos");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const restart = async () => {
    if (!window.confirm("Reiniciar a instância vai desconectar o WhatsApp — você vai precisar escanear um novo QR code. Continuar?")) return;
    setRestarting(true);
    try {
      await api.post("/general-settings/restart-instance");
      toast.success("Instância reiniciada. Vá em Conexões e leia o novo QR.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestarting(false); }
  };

  const handleLogoPick = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo até 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => update({ logo_url: reader.result });
    reader.readAsDataURL(file);
  };

  if (!s) return <div className="p-8 text-sm">Carregando…</div>;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-wider">Ajustes Gerais</h1>
          <p className="text-sm text-[#5C5C59] mt-1">Personalização, atendimento e privacidade da empresa</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="save-general">
          <FloppyDisk size={14} weight="bold" className="mr-1" /> {saving ? "Salvando…" : "Salvar tudo"}
        </Button>
      </div>

      <div className="space-y-3">
        <Section
          title="Reiniciar instância"
          description="Reinicie sua instância para corrigir erros automaticamente. Será necessário ler um novo QR Code (WhatsApp)."
          right={<Button onClick={restart} disabled={restarting} className="w-full rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="restart-instance">
            <ArrowClockwise size={14} weight="bold" className="mr-1" /> {restarting ? "Reiniciando…" : "reiniciar"}
          </Button>}
        />

        <Section
          title="Nome da instância"
          description="Um nome que identifique sua empresa (não é exibido para seus clientes)."
          right={<Input value={s.instance_name || ""} onChange={(e) => update({ instance_name: e.target.value })} className="rounded-none" data-testid="instance-name" />}
        />

        <Section
          title="Logotipo"
          description="Personalize a plataforma com seu próprio logotipo."
          right={<div className="flex items-center gap-2">
            <label className="flex-1 cursor-pointer">
              <input type="file" accept="image/*" onChange={(e) => handleLogoPick(e.target.files?.[0])} className="hidden" data-testid="logo-input" />
              <div className="w-full text-center py-2 bg-[#0F4C3A] hover:bg-[#0A3629] text-white text-sm font-semibold">buscar…</div>
            </label>
            {s.logo_url && (
              <>
                <img src={s.logo_url} alt="logo" className="w-10 h-10 object-contain border border-[#E5E5E0]" />
                <button onClick={() => update({ logo_url: "" })} className="p-2 text-[#DC2626]" title="Remover logo" data-testid="logo-remove">🗑️</button>
              </>
            )}
          </div>}
        />

        <Section
          title="Mensagem de boas vindas"
          description="Mensagem automática enviada sempre que um cliente iniciar uma conversa com você."
          right={<Textarea value={s.welcome_message || ""} onChange={(e) => update({ welcome_message: e.target.value })} placeholder="Olá! Bem vindo à minha empresa." rows={3} className="rounded-none" data-testid="welcome-message" />}
        />

        <Section
          title="Mensagem de finalização"
          description="Mensagem automática enviada sempre que um atendimento for finalizado."
          right={<Textarea value={s.closing_message || ""} onChange={(e) => update({ closing_message: e.target.value })} placeholder="Obrigado pelo contato!" rows={3} className="rounded-none" data-testid="closing-message" />}
        />

        <Section
          title="Assinatura do atendente"
          description="Toda mensagem enviada pelo painel será prefixada com o nome de quem está atendendo (não afeta IA). Use {name}, {full_name} e {message}."
          right={<div className="space-y-2">
            <Switch checked={!!s.signature_enabled} onCheckedChange={(v) => update({ signature_enabled: v })} data-testid="signature-toggle" />
            {s.signature_enabled && (
              <>
                <Input value={s.signature_template || "*{name}*\n{message}"} onChange={(e) => update({ signature_template: e.target.value })} className="rounded-none font-mono text-xs" data-testid="signature-template" />
                <div className="p-2 bg-[#F7F7F5] border-l-[3px] border-[#0F4C3A] text-xs whitespace-pre-wrap font-mono">
                  {(s.signature_template || "*{name}*\n{message}")
                    .replace(/\\n/g, "\n")
                    .replace(/\{full_name\}/g, "Ana Souza")
                    .replace(/\{name\}/g, "Ana")
                    .replace(/\{message\}/g, "Olá, tudo bem?")}
                </div>
              </>
            )}
          </div>}
        />

        <Section
          title="Encerramento de atendimento"
          description="Mensagem enviada quando o atendente clica em Encerrar. Se ativar avaliação, uma segunda mensagem pedindo nota é enviada."
          right={<div className="space-y-2">
            <Textarea value={s.close_message || ""} onChange={(e) => update({ close_message: e.target.value })} placeholder="Ex.: Foi um prazer atender você." rows={2} className="rounded-none text-xs" data-testid="close-message" />
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!s.send_rating_request} onChange={(e) => update({ send_rating_request: e.target.checked })} data-testid="rating-request-toggle" className="accent-[#0F4C3A]" />
              Pedir avaliação (1 a 5 estrelas)
            </label>
            {s.send_rating_request && (
              <Input value={s.rating_prompt || ""} onChange={(e) => update({ rating_prompt: e.target.value })} placeholder="De 1 a 5 estrelas, como foi?" className="rounded-none text-xs" data-testid="rating-prompt" />
            )}
          </div>}
        />

        <Section
          title="Avaliação de atendimento"
          description="Após finalizar um atendimento, envia uma mensagem automática solicitando que o cliente dê uma nota entre 1 e 5."
          right={<Switch checked={!!s.rating_enabled} onCheckedChange={(v) => update({ rating_enabled: v })} data-testid="rating-toggle" />}
        />

        <Section
          title="Distribuição Automática (Beta)"
          description="Defina se as mensagens devem ser distribuídas automaticamente e os intervalos de redistribuição."
          right={<div className="space-y-2">
            <Switch checked={!!s.auto_distribution_enabled} onCheckedChange={(v) => update({ auto_distribution_enabled: v })} data-testid="autodist-toggle" />
            {s.auto_distribution_enabled && (
              <div className="flex items-center gap-2">
                <Input type="number" value={s.redistribution_minutes ?? 5} onChange={(e) => update({ redistribution_minutes: parseInt(e.target.value || "0", 10) })} className="w-20 rounded-none" data-testid="redist-min" />
                <span className="text-xs text-[#5C5C59]">min pra redistribuir</span>
              </div>
            )}
          </div>}
        />

        <Section
          title="Usuário padrão (carteirização)"
          description="Permite designar um usuário padrão para um contato. Quando o contato iniciar um atendimento, ele ficará acessível exclusivamente para esse usuário."
          right={<div className="space-y-2">
            <Switch checked={!!s.default_user_enabled} onCheckedChange={(v) => update({ default_user_enabled: v })} data-testid="defuser-toggle" />
            {s.default_user_enabled && (
              <Textarea value={s.default_user_message || ""} onChange={(e) => update({ default_user_message: e.target.value })} rows={2} className="rounded-none text-xs" placeholder="Aguarde um instante..." />
            )}
          </div>}
        />

        <Section
          title="Ocultar números de telefone"
          description="Oculta os 4 últimos dígitos do telefone (privacidade)."
          right={<Switch checked={!!s.hide_phone_last_digits} onCheckedChange={(v) => update({ hide_phone_last_digits: v })} data-testid="hidephone-toggle" />}
        />

        <Section
          title="Ocultar conversas iniciadas via disparos"
          description="Não exibe as conversas sem interação que foram iniciadas através da opção de Disparos."
          right={<Switch checked={!!s.hide_broadcast_conversations} onCheckedChange={(v) => update({ hide_broadcast_conversations: v })} data-testid="hidebroadcast-toggle" />}
        />

        <Section
          title="Finalizar atendimento por inatividade"
          description="Caso o usuário ou cliente não enviem uma mensagem por determinado período, o atendimento será finalizado automaticamente."
          right={<div className="space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span>Inatividade agente</span>
              <Switch checked={!!s.close_by_inactivity_agent_enabled} onCheckedChange={(v) => update({ close_by_inactivity_agent_enabled: v })} data-testid="closeagent-toggle" />
            </div>
            {s.close_by_inactivity_agent_enabled && (
              <Input type="number" value={s.close_by_inactivity_agent_minutes ?? 30} onChange={(e) => update({ close_by_inactivity_agent_minutes: parseInt(e.target.value || "0", 10) })} className="rounded-none" placeholder="minutos" />
            )}
            <div className="flex items-center justify-between gap-2">
              <span>Inatividade cliente</span>
              <Switch checked={!!s.close_by_inactivity_customer_enabled} onCheckedChange={(v) => update({ close_by_inactivity_customer_enabled: v })} data-testid="closecust-toggle" />
            </div>
            {s.close_by_inactivity_customer_enabled && (
              <Input type="number" value={s.close_by_inactivity_customer_minutes ?? 60} onChange={(e) => update({ close_by_inactivity_customer_minutes: parseInt(e.target.value || "0", 10) })} className="rounded-none" placeholder="minutos" />
            )}
          </div>}
        />

        <Section
          title="🔒 Autenticação de dois fatores (MFA)"
          description="Ativa uma camada extra de segurança. Além da senha, será necessário informar um código temporário do Google Authenticator."
          right={<MFAControl mfaEnabled={!!s.mfa_enabled} onChange={(v) => { update({ mfa_enabled: v }); load(); }} />}
        />

        <Section
          title="Fuso horário padrão"
          description="Fuso usado para exibição de horários e agendamentos."
          right={<Select value={s.timezone || "America/Sao_Paulo"} onValueChange={(v) => update({ timezone: v })}>
            <SelectTrigger className="rounded-none" data-testid="timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-none">
              {TIMEZONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>}
        />

        <Section
          title="Código do país"
          description="Prefixo padrão adicionado quando um telefone é digitado sem código."
          right={<Select value={s.country_code || "+55"} onValueChange={(v) => update({ country_code: v })}>
            <SelectTrigger className="rounded-none" data-testid="country-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-none">
              {COUNTRIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>}
        />
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="save-general-bottom">
          <FloppyDisk size={14} weight="bold" className="mr-1" /> {saving ? "Salvando…" : "Salvar tudo"}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// MFA Control: setup dialog with QR + verify + disable via password.
function MFAControl({ mfaEnabled, onChange }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro"); // intro | qr | disable
  const [setup, setSetup] = useState(null); // { qr_image, secret }
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/setup", {});
      setSetup(data); setStep("qr");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length < 6) { toast.error("Digite o código de 6 dígitos"); return; }
    setBusy(true);
    try {
      await api.post("/mfa/verify", { code });
      toast.success("MFA ativado com sucesso!");
      onChange(true);
      setOpen(false); setStep("intro"); setCode(""); setSetup(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password) { toast.error("Digite sua senha"); return; }
    setBusy(true);
    try {
      await api.post("/mfa/disable", { password });
      toast.success("MFA desativado");
      onChange(false);
      setOpen(false); setStep("intro"); setPassword("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <Switch
          checked={mfaEnabled}
          onCheckedChange={() => { setOpen(true); setStep(mfaEnabled ? "disable" : "intro"); }}
          data-testid="mfa-toggle"
        />
        <span className="text-xs">{mfaEnabled ? "Ativado" : "Desativado"}</span>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border-2 border-[#0F4C3A] max-w-md w-full p-4" onClick={(e) => e.stopPropagation()} data-testid="mfa-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-sm uppercase">🔒 Autenticação 2 Fatores</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5C59]">✕</button>
            </div>
            {step === "intro" && (
              <div className="space-y-3">
                <p className="text-xs text-[#5C5C59]">
                  Instale o app <b>Google Authenticator</b> ou <b>Authy</b> no seu celular. Depois clique em Começar
                  para gerar um QR Code que vinculará sua conta.
                </p>
                <Button onClick={startSetup} disabled={busy} className="w-full rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="mfa-start">
                  {busy ? "Gerando..." : "Começar"}
                </Button>
              </div>
            )}
            {step === "qr" && setup && (
              <div className="space-y-3">
                <p className="text-xs">Escaneie o QR Code no Google Authenticator:</p>
                <img src={setup.qr_image} alt="QR" className="mx-auto w-52 h-52 border border-[#E5E5E0]" data-testid="mfa-qr" />
                <div className="text-[10px] text-center text-[#5C5C59] font-mono select-all">Ou digite: {setup.secret}</div>
                <Label className="text-xs mt-2">Código do app (6 dígitos)</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="rounded-none text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  data-testid="mfa-code"
                  maxLength={6}
                />
                <Button onClick={verify} disabled={busy || code.length < 6} className="w-full rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="mfa-verify">
                  {busy ? "Verificando..." : "Verificar e ativar"}
                </Button>
              </div>
            )}
            {step === "disable" && (
              <div className="space-y-3">
                <p className="text-xs">Digite sua senha atual para desativar o MFA:</p>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none" data-testid="mfa-password" placeholder="Sua senha" />
                <Button onClick={disable} disabled={busy || !password} className="w-full rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="mfa-disable">
                  {busy ? "Desativando..." : "Desativar MFA"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

